import type { ITemplateCard } from '@/models/interfaces'
import { DateTime } from 'luxon'
import * as v from '@/types/valibot.types'
import {
  LibraryFieldType,
  LibraryFilterCardsBy,
  LibrarySortCardsBy
} from './library.enums'
import type { FilterCardsBy, SortCardsBy } from '@/models/models'

export interface LibraryLocale {
  welcome: {
    subHeader: string
    bullet: [
      { mainText: string; secondaryText: string },
      { mainText: string; secondaryText: string },
      { mainText: string; secondaryText: string }
    ]
    continueText: string
  }
  search: string
  apply: string
  save: string
  tab: {
    tabs: string
    tabName: string
    allCards: string
    new: string
    addNew: string
    rename: string
    delete: string
  }
  card: {
    new: string
    newTemplate: string
    newCustom: string
    addNew: string
    delete: string
    edit: string
    customize: string
    updateAlert: string
    newAlert: string
    createdDate: string
    editedDate: string
    field: {
      title: string
      description: string
      name: string
      phone: string
      email: string
      address: string
      city: string
      state: string
      zip: string
    }
    shareLabel: string
    shareDescription: string
    duplicateError: string
    editModalHeader: string
    templateCreateText: string
    templateCreateChooseText: string
    shared: string
    private: string
  }
  filters: {
    legend: string
    allCards: string
    private: string
    sharedByMe: string
    sharedWithMe: string
  }
  sort: {
    legend: string
    newToOld: string
    oldToNew: string
    alphaAsc: string
    alphaDesc: string
  }
  noResults: { found: string; try: string; reset: string }
  upgradeModal: { title: string; content: string; cta: string }
  tabDeleteModal: {
    confirm: { title: string; content: string; cta: string }
    error: { title: string; content: string; cta: string }
  }
  editTabModal: { cta: string; duplicateError: string }
  maxEntryModal: {
    cardTitle: string
    tabTitle: string
    cardContent: string
    tabContent: string
    cta: string
  }
  cardDeleteModal: { title: string; content: string; cta: string }
  templates: [
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    },
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    },
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    },
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    },
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    },
    {
      templateName: string
      templateDescription: string
      description: string
      nameField: string
      phoneField: string
      emailField: string
      addressField: string
      cityField: string
      stateField: string
      zipField: string
    }
  ]
}

// ---------------------------------------------------------------------------
// Shared domain interfaces, schemas, and helpers
// ---------------------------------------------------------------------------
// camelCase properties, `Id` suffix (not `ID`), Luxon DateTime for timestamps.
// Downstream layers (store, composables, components) should use these.

export const TabSchema = v.pipe(
  v.strictObject({
    itemID: v.number(),
    name: v.nullable(v.string()),
    userID: v.number(),
    caseID: v.number(),
    createdWhen: v.pipe(v.string(), v.isoTimestamp()),
    updatedWhen: v.nullable(v.pipe(v.string(), v.isoTimestamp())),
    cardCount: v.number()
  }),
  v.transform((dto) => ({
    itemId: dto.itemID,
    ...(dto.name != null && { name: dto.name }),
    userId: dto.userID,
    caseId: dto.caseID,
    createdWhen: DateTime.fromISO(dto.createdWhen),
    ...(dto.updatedWhen != null && {
      updatedWhen: DateTime.fromISO(dto.updatedWhen)
    }),
    cardCount: dto.cardCount
  }))
)

export type Tab = v.InferOutput<typeof TabSchema>

const FieldSchema = v.pipe(
  v.strictObject({
    itemID: v.number(),
    userID: v.optional(v.number()),
    cardID: v.number(),
    content: v.nullable(v.string()),
    type: v.enum(LibraryFieldType)
  }),
  v.transform((dto) => ({
    itemId: dto.itemID,
    cardId: dto.cardID,
    ...(dto.userID != null && { userId: dto.userID }),
    ...(dto.content != null && { content: dto.content }),
    type: dto.type
  }))
)

export type Field = v.InferOutput<typeof FieldSchema>

export const CardSchema = v.pipe(
  v.strictObject({
    itemID: v.number(),
    tabID: v.number(),
    userID: v.number(),
    caseID: v.optional(v.number()),
    shared: v.boolean(),
    sharedUserID: v.optional(v.number()),
    title: v.nullable(v.string()),
    description: v.optional(v.nullable(v.string())),
    createdWhen: v.pipe(v.string(), v.isoTimestamp()),
    updatedWhen: v.nullable(v.pipe(v.string(), v.isoTimestamp())),
    viewedWhen: v.nullable(v.pipe(v.string(), v.isoTimestamp())),
    tabName: v.nullable(v.string()),
    fields: v.optional(v.array(FieldSchema))
  }),
  v.transform((dto) => ({
    itemId: dto.itemID,
    tabId: dto.tabID,
    userId: dto.userID,
    ...(dto.caseID != null && { caseId: dto.caseID }),
    shared: dto.shared,
    ...(dto.sharedUserID != null && { sharedUserId: dto.sharedUserID }),
    ...(dto.title != null && { title: dto.title }),
    ...(dto.description != null && { description: dto.description }),
    createdWhen: DateTime.fromISO(dto.createdWhen),
    ...(dto.updatedWhen != null && {
      updatedWhen: DateTime.fromISO(dto.updatedWhen)
    }),
    ...(dto.viewedWhen != null && {
      viewedWhen: DateTime.fromISO(dto.viewedWhen)
    }),
    ...(dto.tabName != null && { tabName: dto.tabName }),
    fields: dto.fields
  }))
)

export type Card = v.InferOutput<typeof CardSchema>

// ---------------------------------------------------------------------------
// GetTabs / GetTab
// ---------------------------------------------------------------------------

export const GetTabsResponseSchema = v.array(TabSchema)

export type GetTabsResponseInput = v.InferInput<typeof GetTabsResponseSchema>

export type GetTabsResponse = v.InferOutput<typeof GetTabsResponseSchema>

export interface GetTabsResult {
  data: GetTabsResponse
  isCache: boolean
  isStale: boolean
}

// * request schemas get transformed to match API contract
export const GetTabRequestSchema = v.pipe(
  v.strictObject({ tabId: v.number() }),
  v.transform(({ tabId }) => ({ tabID: tabId }))
)

// * so when parsing the request, we get the API version
export type GetTabRequest = v.InferInput<typeof GetTabRequestSchema>

export const GetTabResponseSchema = TabSchema

export type GetTabResponseInput = v.InferInput<typeof GetTabResponseSchema>

export type GetTabResponse = Tab

export type GetTabResult = {
  data: GetTabResponse
  isCache: boolean
  isStale: boolean
} | null

// ---------------------------------------------------------------------------
// CreateTab / EditTab / DeleteTab
// ---------------------------------------------------------------------------

export const CreateTabRequestSchema = v.strictObject({ name: v.string() })

export type CreateTabRequest = v.InferInput<typeof CreateTabRequestSchema>

export const CreateTabResponseSchema = v.void()

export type CreateTabResponseInput = v.InferInput<
  typeof CreateTabResponseSchema
>

export type CreateTabResponse = v.InferOutput<typeof CreateTabResponseSchema>

export const EditTabRequestSchema = v.pipe(
  v.strictObject({ itemId: v.number(), name: v.string() }),
  v.transform((dto) => ({ itemID: dto.itemId, name: dto.name }))
)

export type EditTabRequest = v.InferInput<typeof EditTabRequestSchema>

export const EditTabResponseSchema = v.void()

export type EditTabResponseInput = v.InferInput<typeof EditTabResponseSchema>

export type EditTabResponse = v.InferOutput<typeof EditTabResponseSchema>

export const DeleteTabRequestSchema = v.pipe(
  v.strictObject({ tabId: v.number() }),
  v.transform((dto) => ({ tabID: dto.tabId }))
)

export type DeleteTabRequest = v.InferInput<typeof DeleteTabRequestSchema>

export const DeleteTabResponseSchema = v.void()

export type DeleteTabResponseInput = v.InferInput<
  typeof DeleteTabResponseSchema
>

export type DeleteTabResponse = v.InferOutput<typeof DeleteTabResponseSchema>

// ---------------------------------------------------------------------------
// GetAllCards / GetCard / ViewCard
// ---------------------------------------------------------------------------

export const GetAllCardsRequestSchema = v.pipe(
  v.strictObject({
    tabId: v.optional(v.number()),
    searchTerm: v.optional(v.string()),
    filterBy: v.optional(v.enum(LibraryFilterCardsBy)),
    sortBy: v.optional(v.enum(LibrarySortCardsBy))
  }),
  v.transform((dto) => ({
    tabID: dto.tabId ?? 0,
    searchTerm: dto.searchTerm ?? null,
    filterBy: dto.filterBy ?? LibraryFilterCardsBy.Default,
    sortBy: dto.sortBy ?? LibrarySortCardsBy.Default
  }))
)

export type GetAllCardsRequest = v.InferInput<typeof GetAllCardsRequestSchema>

export const GetAllCardsResponseSchema = v.array(CardSchema)

export type GetAllCardsResponseInput = v.InferInput<
  typeof GetAllCardsResponseSchema
>

export type GetAllCardsResponse = v.InferOutput<
  typeof GetAllCardsResponseSchema
>

export const GetCardRequestSchema = v.pipe(
  v.strictObject({ cardId: v.number() }),
  v.transform((dto) => ({ cardID: dto.cardId }))
)

export type GetCardRequest = v.InferInput<typeof GetCardRequestSchema>

export const GetCardResponseSchema = CardSchema

export type GetCardResponseInput = v.InferInput<typeof GetCardResponseSchema>

export type GetCardResponse = v.InferOutput<typeof GetCardResponseSchema>

export const ViewCardRequestSchema = v.pipe(
  v.strictObject({ cardId: v.number() }),
  v.transform((dto) => ({ cardID: dto.cardId }))
)

export type ViewCardRequest = v.InferInput<typeof ViewCardRequestSchema>

export const ViewCardResponseSchema = CardSchema

export type ViewCardResponseInput = v.InferInput<typeof ViewCardResponseSchema>

export type ViewCardResponse = v.InferOutput<typeof ViewCardResponseSchema>

// ---------------------------------------------------------------------------
// CreateCard / EditCard / DeleteCard
// ---------------------------------------------------------------------------

const CreateCardFieldRequestSchema = v.strictObject({
  content: v.string(),
  type: v.enum(LibraryFieldType)
})

export const CreateCardRequestSchema = v.pipe(
  v.strictObject({
    tabId: v.number(),
    shared: v.boolean(),
    title: v.string(),
    description: v.optional(v.string()),
    fields: v.optional(v.array(CreateCardFieldRequestSchema))
  }),
  v.transform((dto) => ({
    tabID: dto.tabId,
    shared: dto.shared,
    title: dto.title,
    description: dto.description ?? null,
    fields: dto.fields ?? null
  }))
)

export type CreateCardRequest = v.InferInput<typeof CreateCardRequestSchema>

export const CreateCardResponseSchema = v.void()

export type CreateCardResponseInput = v.InferInput<
  typeof CreateCardResponseSchema
>

export type CreateCardResponse = v.InferOutput<typeof CreateCardResponseSchema>

const EditCardFieldRequestSchema = v.strictObject({
  itemId: v.number(),
  cardId: v.number(),
  content: v.string(),
  type: v.enum(LibraryFieldType)
})

export const EditCardRequestSchema = v.pipe(
  v.strictObject({
    itemId: v.number(),
    tabId: v.number(),
    shared: v.boolean(),
    title: v.string(),
    description: v.optional(v.string()),
    fields: v.optional(v.array(EditCardFieldRequestSchema))
  }),
  v.transform((dto) => ({
    itemID: dto.itemId,
    tabID: dto.tabId,
    shared: dto.shared,
    title: dto.title,
    description: dto.description ?? null,
    fields: dto.fields ?? null
  }))
)

export type EditCardRequest = v.InferInput<typeof EditCardRequestSchema>

export const EditCardResponseSchema = v.void()

export type EditCardResponseInput = v.InferInput<typeof EditCardResponseSchema>

export type EditCardResponse = v.InferOutput<typeof EditCardResponseSchema>

export const DeleteCardRequestSchema = v.pipe(
  v.strictObject({ cardId: v.number() }),
  v.transform((dto) => ({ cardID: dto.cardId }))
)

export type DeleteCardRequest = v.InferInput<typeof DeleteCardRequestSchema>

export const DeleteCardResponseSchema = v.void()

export type DeleteCardResponseInput = v.InferInput<
  typeof DeleteCardResponseSchema
>

export type DeleteCardResponse = v.InferOutput<typeof DeleteCardResponseSchema>

// Repository result types - mirroring tab result pattern (data + isCache + isStale)
export interface RepositoryTabsResult {
  tabs: Tab[]
  isCache: boolean
  isStale: boolean
}

export interface CachedCard extends Omit<
  Card,
  'createdWhen' | 'updatedWhen' | 'viewedWhen'
> {
  createdWhen: string
  updatedWhen?: string
  viewedWhen?: string
}

export type GetAllCardsCachedResponse = CachedCard[]

export interface GetAllCardsResult {
  data: GetAllCardsResponse
  isCache: boolean
  isStale: boolean
}

export type GetCardCachedResponse = CachedCard

export interface GetCardResult {
  data: GetCardResponse
  isCache: boolean
  isStale: boolean
}

export interface IInfoLibraryState {
  tabs: Tab[]
  selectedTabId: number
  addTabErrorMessage: string | null
  tabForEdit: Tab
  editTabErrorMessage: string | null
  deleteTabErrorMessage: string | null
  selectedCardId: number | null
  selectedCardIdForUpdate: number | null
  addCardErrorMessage: string | null
  showEditCard: boolean
  cardForEdit: Card
  editCardErrorMessage: string | null
  deleteCardErrorMessage: string | null
  cards: Card[]
  searchTerm: string | null
  filterCardsBy: FilterCardsBy
  sortCardsBy: SortCardsBy
  tabCards: Card[]
  currentTemplate: ITemplateCard | null
  selectedCardSummary: Card | null
  selectedCard: Card | null
  isFetchingDetails: boolean
}
